import { commaListSchema } from "@birdwatch/common";
import { createFileRoute, Navigate, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { APP_ROUTERS } from "@/constants/app-routers";
import { keepCampaignSearch } from "@/features/presale";
import { maskSearchFragment } from "@/features/shared/route-masking";

const searchSchema = z.object({
  /**
   * PRESENCE, not value, sends this campaign link into the presale funnel — the bare
   * `?presale` parses to `""`, so a truthiness check would never fire.
   */
  presale: z.string().optional(),
  planKey: z.string().optional(),
  discountCode: z.string().optional(),
  addOns: commaListSchema,
  referId: z.string().optional(),
  referredBy: z.string().optional(),
  ...maskSearchFragment,
});

function RouteComponent() {
  const { checkoutId } = Route.useParams();
  return (
    <Navigate
      to={APP_ROUTERS.MY_PLACES}
      search={(prev) => ({
        ...prev,
        checkoutId,
        addOns: (prev as { addOns?: string[] })?.addOns || [],
      })}
      replace
    />
  );
}

export const Route = createFileRoute(
  "/(member)/(protected)/my/checkout/$checkoutId",
)({
  validateSearch: searchSchema,
  /**
   * The presale hand-off. It lives in `beforeLoad` — not the component — so it fires
   * before `ProtectedProvider` renders and intercepts a signed-out visitor into the
   * MEMBER join funnel: `/presale/*` is public, and a campaign link has to work
   * logged out. `/presale/sign-up` forwards an already-signed-in buyer to the home
   * step itself, so one destination covers both.
   *
   * The path param and presale's `checkoutLinkId` are the same `CheckoutLink.id` —
   * the translation is a rename, not a lookup.
   */
  beforeLoad: ({ params, search }) => {
    if (search.presale === undefined) return;
    throw redirect({
      to: APP_ROUTERS.PRESALE_SIGN_UP,
      search: keepCampaignSearch({
        checkoutLinkId: params.checkoutId,
        discountCode: search.discountCode,
        referId: search.referId,
        referredBy: search.referredBy,
      }),
      replace: true,
    });
  },
  component: RouteComponent,
});
